import { useMemo } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

/** A user the operator can pick. `value` is what callers submit (the e-mail / username). */
export interface UserPick {
    value: string;
    name: string;
    email: string;
    phone: string;
}

interface Option extends UserPick {
    label: string; // "Name (email)" — accessible name + closed-control text
    search: string; // lower-cased name + email + phone, for the dropdown filter
}

export interface UserSelectProps {
    users: UserPick[];
    value: string;
    onChange: (value: string) => void;
    inputId?: string;
    placeholder?: string;
    isDisabled?: boolean;
    className?: string;
}

/**
 * Shared, searchable user picker. Searches by name, e-mail OR phone (not just the
 * visible label), and shows each user as a name with a muted "email · phone" line.
 * Used everywhere an operator selects an existing user, for a consistent, functional
 * experience instead of bare e-mail lists.
 */
export function UserSelect({
    users,
    value,
    onChange,
    inputId,
    placeholder,
    isDisabled,
    className,
}: UserSelectProps) {
    const { t } = useTranslation();
    const options = useMemo<Option[]>(
        () =>
            users
                .map((u) => ({
                    ...u,
                    label: u.name ? `${u.name} (${u.email})` : u.email,
                    search: `${u.name} ${u.email} ${u.phone}`.toLowerCase(),
                }))
                .sort((a, b) => a.label.localeCompare(b.label, 'nb')),
        [users],
    );

    return (
        <Select<Option, false>
            inputId={inputId}
            options={options}
            value={options.find((o) => o.value === value) ?? null}
            onChange={(o) => onChange(o?.value ?? '')}
            isDisabled={isDisabled}
            classNamePrefix="dkn-rs"
            className={className}
            placeholder={placeholder ?? t('common.selectPlaceholder')}
            noOptionsMessage={() => t('common.noOptions')}
            filterOption={(opt, input) => opt.data.search.includes(input.toLowerCase())}
            formatOptionLabel={(o, meta) => {
                if (meta.context !== 'menu') {
                    return <span>{o.name ? `${o.name} (${o.email})` : o.email}</span>;
                }
                const metaLine = [o.name ? o.email : '', o.phone].filter(Boolean).join(' · ');
                return (
                    <div className="user-option">
                        <div className="user-option__name">{o.name || o.email}</div>
                        {metaLine && <div className="user-option__meta">{metaLine}</div>}
                    </div>
                );
            }}
        />
    );
}
